import { request } from '@saasplatform/api';
import { getTenantApiBaseUrl } from '@saasplatform/config';
import type {
  OneClickLoginResponse,
  PublicTenantInfo,
  RegisterCaptchaChallengeResponse,
  RegisterTenantUserParams,
  SendRegisterCodeParams,
  TenantPasswordResetParams,
  TenantLoginResponse,
  TenantUser,
  VerifyRegisterCaptchaParams,
} from '@saasplatform/saas-core';

export type {
  OneClickLoginResponse,
  PublicTenantInfo,
  RegisterCaptchaChallengeResponse,
  RegisterTenantUserParams,
  SendRegisterCodeParams,
  TenantLoginResponse,
  TenantPasswordResetParams,
  TenantUser,
  VerifyRegisterCaptchaParams,
} from '@saasplatform/saas-core';

let publicTenantCache: PublicTenantInfo | null = null;
let publicTenantPromise: Promise<PublicTenantInfo> | null = null;

// 一键登录
export const oneClickLogin = (tempToken: string): Promise<OneClickLoginResponse> => {
  return request.get('/tenant-auth/one-click-login', {
    baseURL: getTenantApiBaseUrl(),
    params: { temp_token: tempToken },
  });
};

export const tenantLogin = (params: {
  username: string;
  password: string;
}): Promise<TenantLoginResponse> => {
  return request.post('/tenant-auth/login', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const getTenantCurrentUser = (): Promise<TenantUser> => {
  return request.get('/tenant-auth/me', {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const tenantLogout = async () => {
  await request.post(
    '/tenant-auth/logout',
    {},
    {
      baseURL: getTenantApiBaseUrl(),
    },
  );
};

export const getPublicTenant = (): Promise<PublicTenantInfo> => {
  if (publicTenantCache) {
    return Promise.resolve(publicTenantCache);
  }

  if (!publicTenantPromise) {
    publicTenantPromise = request.get('/tenant-auth/public-tenant', {
      baseURL: getTenantApiBaseUrl(),
    })
      .then((tenant) => {
        const resolvedTenant = tenant as unknown as PublicTenantInfo;
        publicTenantCache = resolvedTenant;
        return resolvedTenant;
      })
      .finally(() => {
        publicTenantPromise = null;
      });
  }

  return publicTenantPromise;
};

export const createRegisterCaptcha = (params: {
  deviceFingerprint?: string;
}): Promise<RegisterCaptchaChallengeResponse> => {
  return request.post('/tenant-auth/register-captcha', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const verifyRegisterCaptcha = (params: VerifyRegisterCaptchaParams): Promise<{ captchaToken: string; expiresInSeconds: number }> => {
  return request.post('/tenant-auth/register-captcha/verify', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const sendRegisterCode = (params: SendRegisterCodeParams): Promise<{ message: string }> => {
  return request.post('/tenant-auth/send-register-code', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const sendTenantPasswordResetCode = (params: {
  phone: string;
  deviceFingerprint?: string;
}): Promise<{ message: string }> => {
  return request.post('/tenant-auth/send-password-reset-code', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const resetTenantPassword = (params: TenantPasswordResetParams): Promise<{ message: string }> => {
  return request.post('/tenant-auth/reset-password', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};

export const registerTenantUser = (params: RegisterTenantUserParams): Promise<{ message: string }> => {
  return request.post('/tenant-auth/register', params, {
    baseURL: getTenantApiBaseUrl(),
  });
};
